import db, { getOfflineSiteMapUploads } from '../db/db';
import positionableApi from './api/positionable';
import uploadApi from './api/upload';
import { isLatLngInMap, latLngToPixel } from './geo';

export interface FindingPlacementInput {
  lat: number;
  lng: number;
  project_upload_ids: number[];
}

/** project_inspection_uploads rows cached for this inspection, keyed by project_upload_id. */
const inspectionUploadsByMap = async (projectId: number, inspectionId: number) => {
  const row = await db.projectInspectionUploads
    .where('project_id')
    .equals(Number(projectId))
    .and((item: any) => item.inspection_id === Number(inspectionId))
    .first();

  return new Map<number, any>(
    (row?.data || []).map((upload: any) => [
      Number(upload.project_upload_id),
      upload,
    ]),
  );
};

/** Georeferenced maps attached to this inspection, read from the download cache. */
const calibratedMaps = async (projectId: number, inspectionId: number) => {
  const [uploads, byMap] = await Promise.all([
    getOfflineSiteMapUploads(projectId),
    inspectionUploadsByMap(projectId, inspectionId),
  ]);

  return {
    byMap,
    maps: (uploads || []).filter(
      (upload: any) => upload.gis_calibrated && byMap.has(Number(upload.id)),
    ),
  };
};

/** Offline twin of projectApi.checkCoordinateOnSiteMaps — the same containment test, run locally. */
export const checkCoordinateOffline = async (
  projectId: number,
  inspectionId: number,
  lat: number,
  lng: number,
) => {
  const { maps } = await calibratedMaps(projectId, inspectionId);

  return {
    hasCalibratedMaps: maps.length > 0,
    containingMaps: maps
      .filter((upload: any) => isLatLngInMap(lat, lng, upload))
      .map((upload: any) => ({
        project_upload_id: upload.id,
        name: upload.upload?.name ?? null,
        site_map_type: upload.site_map_type,
      })),
  };
};

/**
 * Offline twin of projectApi.placeFindingOnMaps.
 *
 * Writes the same row the server's placeFinding does, through positionableApi so the placement
 * queues and syncs like any other positionable — including the finding-id rewrite the sync does
 * once a finding created offline receives its real id.
 */
export const placeFindingOffline = async (
  projectId: number,
  findingId: number,
  { lat, lng, project_upload_ids }: FindingPlacementInput,
  inspectionId?: number,
) => {
  // A finding created offline is stored with `inspection_id`; only a downloaded one carries the
  // server's `initial_inspection_id`. The caller's own id is preferred over guessing between them.
  const finding = inspectionId ? null : await db.findings.get(Number(findingId));
  const targetInspectionId =
    inspectionId ??
    finding?.initial_inspection_id ??
    finding?.inspection_id ??
    finding?.InspectionFindingModel?.inspection_id;

  if (!targetInspectionId) {
    throw new Error('This finding is not saved on this device, so it cannot be placed.');
  }

  const { maps, byMap } = await calibratedMaps(projectId, targetInspectionId);
  const selected = new Set(project_upload_ids.map(Number));
  const placed: any[] = [];

  for (const upload of maps) {
    if (!selected.has(Number(upload.id)) || !isLatLngInMap(lat, lng, upload)) {
      continue;
    }

    // The cached image is the only source of the dimensions the server reads from storage.
    const [width, height] = await uploadApi
      .getDimensions(upload.upload?.GUID)
      .catch(() => []);
    const pixel = width && height ? latLngToPixel(lat, lng, width, height, upload) : null;

    if (!pixel) {
      continue;
    }

    const [x, y] = pixel;
    const row = {
      target_type: 'project_inspection_uploads',
      target_id: byMap.get(Number(upload.id)).id,
      positionable_type: 'findings',
      positionable_id: Number(findingId),
      position_x: x,
      position_y: y,
      positionable_config: '{}',
      start_inspection_id: targetInspectionId,
      x_pct: (x / width) * 100,
      y_pct: (y / height) * 100,
      latitude: lat,
      longitude: lng,
    };

    await positionableApi.bulkUpdate(row.target_type, row.target_id, [row]);
    placed.push(row);
  }

  if (!placed.length) {
    throw new Error('Coordinate is not within any of the selected maps.');
  }

  return placed;
};
